import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Container, TextField, Box, Button, IconButton, Theme } from '@mui/material';
import { Page } from '../../components';
import { ConfirmationDialogue } from '../../components/modal/ConfirmationDialogue';
import { DraggableTable } from '../../components/fullTable/DraggableTable';
import { MobileDraggableTable } from '../../components/fullTable/MobileDraggableTable';
import { SmpBundlesAPI } from '../../fetch/smp-bundles';
import { useToastContext } from '../../hooks';
import { ISmpBundle } from '../../models/smp-bundles';
import { CellProps } from 'react-table';
import { Add, Delete } from '@mui/icons-material';
import '../smp/pages/include/styles/smp.css';

export const SmpBundles: FC = () => {
  const { showToast } = useToastContext();
  const [rows, setRows] = useState<(ISmpBundle & { id: string | number })[]>([]);
  const [newElementIndex, setNewElementIndex] = useState(-1);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [toDelete, setToDelete] = useState<ISmpBundle | null>(null);
  const [hasChanges, setHasChanges] = useState(false);

  const didFetch = useRef(false);
  const rowsRef = useRef(rows);
  rowsRef.current = rows;

  const fetchBundles = useCallback(async () => {
    setIsLoading(true);
    try {
      const bundles = await SmpBundlesAPI.getAll();
      const sorted = bundles.sort((a, b) => a.displayOrder - b.displayOrder);
      const mapped = sorted.map((b, index) => ({
        ...b,
        id: b.smpBundleId,
        displayOrder: index + 1
      }));
      setRows(mapped);
      setHasChanges(false);
    } catch (error) {
      showToast('error', error instanceof Error ? error.message : 'Failed to load SMP bundles.');
    } finally {
      setIsLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    if (!didFetch.current) {
      fetchBundles();
      didFetch.current = true;
    }
  }, [fetchBundles]);

  const handleReorder = (input: any) => {
    if (typeof input === 'function') {
      setRows(prevData => {
        const newData = input(prevData);
        const updated = newData.map((item: any, index: number) => ({
          ...item,
          displayOrder: index + 1
        }));
        setHasChanges(true);
        return updated;
      });
    } else if (Array.isArray(input)) {
      const updated = input.map((item, index) => ({
        ...item,
        displayOrder: index + 1
      }));
      setRows(updated);
      setHasChanges(true);
    }
  };

  const updateRow = useCallback((index: number, field: keyof ISmpBundle, value: string) => {
    setRows(prev => {
      const copy = [...prev];
      copy[index] = { ...copy[index], [field]: value };
      return copy;
    });
    setHasChanges(true);
  }, []);


  const handleSave = useCallback(async () => {
    setIsSaving(true);
    try {
      const payload = rowsRef.current.map(({ id, ...rest }) => rest);
      const saved = await SmpBundlesAPI.upsert(payload);
      const mapped = saved.map(b => ({ ...b, id: b.smpBundleId }));
      setRows(mapped);
      setHasChanges(false);
      setNewElementIndex(-1);
      showToast('success', 'Successfully saved SMP bundles.');
    } catch (error) {
      showToast('error', error instanceof Error ? error.message : 'Failed to save SMP bundles.');
    } finally {
      setIsSaving(false);
    }
  }, [showToast]);


  const handleAddNew = useCallback(() => {
    const newItem = {
      ...ISmpBundle.getDefault(),
      id: new Date().valueOf(),
      displayOrder: rowsRef.current.length + 1
    };
    setRows(r => [...r, newItem]);
    setNewElementIndex(rowsRef.current.length);
    setHasChanges(true);
  }, []);

  const columns = useMemo(() => [
    {
      id: 'rowIndex',
      Header: '#',
      Cell: ({ cell }: CellProps<ISmpBundle>) => (
        <Box component="span" sx={{
          width: 32,
          minWidth: 32,
          maxWidth: 32,
          textAlign: 'right',
          fontFamily: 'monospace',
          p: '0 2px'
        }}>
          {cell.row.index + 1}
        </Box>
      ),
      width: 32,
      minWidth: 32,
      maxWidth: 32,
      disableSortBy: true,
    },
    {
      accessor: 'name',
      Header: () => (
        <Box sx={{ minWidth: 250, fontWeight: 'bold' }}>
          Bundle Name
        </Box>
      ),
      Cell: ({ cell }: CellProps<ISmpBundle>) => {
        const idx = cell.row.index;
        return (
          <Box sx={{ minWidth: 250 }}>
            <TextField
              fullWidth
              inputProps={{ className: 'smp-bundle-name-input' }}
              autoFocus={idx === newElementIndex}
              placeholder="Bundle Name"
              value={cell.row.original.name ?? ''}
              onChange={e => updateRow(idx, 'name', e.target.value)}
            />
          </Box>
        );
      },
    },
    {
      accessor: 'actions',
      Header: () => (
        <Box
          display="flex"
          justifyContent="end"
          sx={{
            '& > *:not(:last-child)': { marginRight: (theme: Theme) => theme.spacing(1) },
            '.MuiButton-root.Mui-disabled': {
              border: '1px solid rgba(140, 155, 147, 0.5)',
              color: 'rgb(255 255 255 / 26%)',
              backgroundColor: '#19667E',
            },
          }}
        >
          <Button
            sx={(theme: Theme) => ({
              color: '#fff',
              borderColor: 'rgb(255 255 255 / 50%)',
              '&:hover': {
                color: theme.frankie.searchBlue,
                borderColor: '#ffffff',
                backgroundColor: '#ffffff'
              },
            })}
            startIcon={<Add />}
            variant="outlined"
            onClick={handleAddNew}
          >
            Add
          </Button>
          <Button
            sx={(theme: Theme) => ({
              color: '#fff',
              borderColor: 'rgb(255 255 255 / 50%)',
              backgroundColor: theme.palette.primary.main,
              '&:hover': { backgroundColor: theme.palette.primary.dark },
            })}
            disabled={!hasChanges}
            variant="contained"
            onClick={handleSave}
          >
            {isSaving ? 'Saving...' : 'Save'}
          </Button>
        </Box>
      ),
      Cell: ({ cell }: CellProps<ISmpBundle>) => (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <IconButton onClick={() => setToDelete(cell.row.original)}>
            <Delete />
          </IconButton>
        </Box>
      ),
      sort: false,
    },
  ], [newElementIndex, hasChanges, isSaving, updateRow, handleAddNew, handleSave]);

  const handleSort = () => { };

  return (
    <Page title="SMP Bundles">
      <Container maxWidth={false} disableGutters>
        <DraggableTable
          isLoading={isLoading}
          columns={columns}
          data={rows}
          setData={handleReorder}
          handleSort={handleSort}
          mobileProps={{
            fields: columns
              .filter(col => col.Header)
              .filter(col => (col as any).accessor !== 'actions')
              .map(col => ({
                name: col.Header as string,
                accessor: (col as any).accessor,
                Cell: (col as any).Cell
              })),
            mobileAccordionLabel: (original: any) => `${original?.name ?? ''}`,
          }}
          ResponsiveComponent={MobileDraggableTable}
        />
      </Container>

      {toDelete && (
        <ConfirmationDialogue
          open
          id="delete-smp-bundle"
          title={`Delete SMP Bundle ${toDelete.name}`}
          onClose={() => setToDelete(null)}
          onConfirm={async () => {
            try {
              await SmpBundlesAPI.delete(toDelete.smpBundleId);
              const newData = rowsRef.current
                .filter(r => r.smpBundleId !== toDelete.smpBundleId)
                .map((item, idx) => ({ ...item, displayOrder: idx + 1 }));
              setRows(newData);

              await handleSave();

            } catch (error) {
              showToast('error', error instanceof Error ? error.message : 'Failed to delete and save new order.');
            } finally {
              setToDelete(null);
            }
          }}
        />
      )}

    </Page>
  );
};